import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/core/utils';
import { BillingCycle } from './types';
import { YEARLY_SAVINGS_PERCENT } from './constants';

interface BillingToggleProps {
  billingCycle: BillingCycle;
  onToggle: (cycle: BillingCycle) => void;
}

export const BillingToggle: React.FC<BillingToggleProps> = ({
  billingCycle,
  onToggle,
}) => {
  return (
    <div className="flex items-center justify-center gap-4">
      <span
        className={cn(
          'text-sm font-medium transition-colors duration-300 cursor-pointer',
          billingCycle === 'monthly'
            ? 'text-foreground'
            : 'text-muted-foreground hover:text-foreground/70'
        )}
        onClick={() => onToggle('monthly')}
      >
        Monthly
      </span>

      <Switch
        checked={billingCycle === 'yearly'}
        onCheckedChange={(checked) => onToggle(checked ? 'yearly' : 'monthly')}
        className={cn(
          'data-[state=checked]:bg-gradient-to-r data-[state=checked]:from-grow data-[state=checked]:to-primary',
          'transition-all duration-300'
        )}
      />

      <span
        className={cn(
          'text-sm font-medium transition-colors duration-300 cursor-pointer',
          billingCycle === 'yearly'
            ? 'text-foreground'
            : 'text-muted-foreground hover:text-foreground/70'
        )}
        onClick={() => onToggle('yearly')}
      >
        Yearly
      </span>

      <AnimatePresence mode="wait">
        <motion.div
          key={billingCycle}
          initial={{ opacity: 0, scale: 0.9, x: -10 }}
          animate={{ opacity: 1, scale: 1, x: 0 }}
          exit={{ opacity: 0, scale: 0.9, x: 10 }}
          transition={{ duration: 0.2 }}
        >
          <Badge
            variant="outline"
            className={cn(
              'ml-1 transition-all duration-300 font-medium',
              billingCycle === 'yearly'
                ? 'bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/30'
                : 'bg-muted text-muted-foreground border-border opacity-60'
            )}
          >
            <Sparkles className="w-3 h-3 mr-1" />
            Save {YEARLY_SAVINGS_PERCENT}%
          </Badge>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default BillingToggle;
